import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import dayjs from 'utils/dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import { createServiceDate, patchServiceDate } from 'api/requests/contract-processing-requests';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import {
  getToBeProcessedAction,
  setContractProcessingDataAction,
  setContractProcessingErrorAction,
  setBulkConfirmErrorsAction,
  bulkConfirmServiceDatesAction,
} from 'store/actions/contract-processing-actions';
import {
  contractProcessingItemsSelector,
  contractProcessingTotalItemsSelector,
  contractProcessingErrorSelector,
  contractProcessingBulkConfirmErrorsSelector,
} from 'store/selectors/contract-processing-selectors';
import {
  ToBeProcessedItem,
  CreateServiceDateRequest,
  PatchServiceDateRequest,
  BulkConfirmServiceDatesRequest,
} from '../types';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_INDEX } from '../constants';

const DEFAULT_SORTING: SortingState = [{ id: 'employeeName', desc: false }];
const SUCCESS_MESSAGE_TIMEOUT = 3000;

export interface UseToBeProcessedDataReturn {
  items: ToBeProcessedItem[];
  totalItems: number;
  error: boolean;
  clearError: () => void;
  pagination: { pageIndex: number; pageSize: number };
  setPagination: (p: { pageIndex: number; pageSize: number }) => void;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
  saveField: (row: ToBeProcessedItem, field: string, value: string | number) => Promise<void>;
  selectedRows: number[];
  setSelectedRows: (ids: number[]) => void;
  successMessage: string | null;
  clearSuccessMessage: () => void;
  errorMessage: string | null;
  clearErrorMessage: () => void;
  // Select-all + bulk confirm
  selectAllMode: boolean;
  excludedIds: Set<number>;
  currentPageSelectedRows: number[];
  isAllOnPageSelected: boolean;
  isSomeSelected: boolean;
  handleSelectAllOnPage: () => void;
  handleSelectionChange: (ids: (string | number)[]) => void;
  handleClearAll: () => void;
  getSelectionCount: () => number;
  handleBulkConfirm: () => Promise<void>;
  bulkConfirmLoading: boolean;
  bulkConfirmErrors: Record<number, string>;
}

function mapSortingToApi(sorting: SortingState): { sortField: string; sortDirection: 'asc' | 'desc' } {
  if (sorting.length === 0) {
    return { sortField: DEFAULT_SORTING[0].id, sortDirection: 'asc' };
  }
  const { id, desc } = sorting[0];
  return { sortField: id, sortDirection: desc ? 'desc' : 'asc' };
}

export const useToBeProcessedData = (
  companyId: number | null,
  week: number,
  year: number,
  date?: string | null,
  employeeIds?: number[],
  costCenterId?: number | null,
  locationId?: number | null,
  workerClass?: string | null,
  functionId?: number | null,
): UseToBeProcessedDataReturn => {
  const dispatch = useAppDispatch();

  // Stabilize employeeIds array reference to prevent infinite re-renders
  const employeeIdsKey = JSON.stringify(employeeIds ?? []);

  const items = useAppSelector(contractProcessingItemsSelector);
  const totalItems = useAppSelector(contractProcessingTotalItemsSelector);
  const error = useAppSelector(contractProcessingErrorSelector);
  const bulkConfirmErrors = useAppSelector(contractProcessingBulkConfirmErrorsSelector);

  const [pagination, setPagination] = useState({ pageIndex: DEFAULT_PAGE_INDEX, pageSize: DEFAULT_PAGE_SIZE });
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const [bulkConfirmLoading, setBulkConfirmLoading] = useState(false);

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevCompanyIdRef = useRef<number | null>(null);
  const itemsRef = useRef(items);
  const totalItemsRef = useRef(totalItems);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    totalItemsRef.current = totalItems;
  }, [totalItems]);

  const clearSuccessMessage = useCallback(() => {
    setSuccessMessage(null);
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch(setContractProcessingErrorAction(false));
  }, [dispatch]);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const setSuccessWithAutoDismiss = useCallback((message: string) => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    setSuccessMessage(message);
    successTimerRef.current = setTimeout(() => {
      setSuccessMessage(null);
      successTimerRef.current = null;
    }, SUCCESS_MESSAGE_TIMEOUT);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Single effect: fetch data when companyId, pagination, sorting, week, or year changes
  useEffect(() => {
    if (!companyId) return;

    const companyChanged = prevCompanyIdRef.current !== null && prevCompanyIdRef.current !== companyId;
    prevCompanyIdRef.current = companyId;

    let fetchPagination = pagination;
    let fetchSorting = sorting;

    if (companyChanged) {
      fetchPagination = { pageIndex: DEFAULT_PAGE_INDEX, pageSize: DEFAULT_PAGE_SIZE };
      fetchSorting = DEFAULT_SORTING;
      setPagination(fetchPagination);
      setSorting(fetchSorting);
      setSelectedRows([]);
      setSuccessMessage(null);
      setErrorMessage(null);
      setSelectAllMode(false);
      setExcludedIds(new Set());
    }

    const { sortField, sortDirection } = mapSortingToApi(fetchSorting);
    const formattedDate = date ? dayjs(new Date(date)).format('DD/MM/YYYY') : undefined;
    const promise = dispatch(
      getToBeProcessedAction({
        companyId,
        filters: {
          pageIndex: fetchPagination.pageIndex + 1, // Convert 0-based (TanStack) to 1-based (backend)
          pageSize: fetchPagination.pageSize,
          sortField,
          sortDirection,
          week,
          year,
          date: formattedDate,
          employeeIds: employeeIds?.length ? employeeIds : undefined,
          costCenterId: costCenterId || undefined,
          locationId: locationId || undefined,
          workerClass: workerClass || undefined,
          functionId: functionId || undefined,
        },
      }),
    );

    // eslint-disable-next-line consistent-return
    return () => {
      promise.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    pagination,
    sorting,
    week,
    year,
    date,
    employeeIdsKey,
    costCenterId,
    locationId,
    workerClass,
    functionId,
    dispatch,
  ]);

  // Reset selection and clear bulk confirm errors when week, year, sorting, or filters change
  useEffect(() => {
    setSelectedRows([]);
    setSelectAllMode(false);
    setExcludedIds(new Set());
    dispatch(setBulkConfirmErrorsAction({}));
  }, [week, year, sorting, date, employeeIdsKey, costCenterId, locationId, workerClass, functionId, dispatch]);

  const saveField = useCallback(
    async (row: ToBeProcessedItem, field: string, value: string | number) => {
      if (!companyId) return;
      if (field === 'costCenterId') return;

      try {
        if (row.serviceDateId === null) {
          const request: CreateServiceDateRequest = {
            contractDateId: row.contractDateId,
            [field]: value,
          };
          const updatedItem = await createServiceDate(request, companyId);

          dispatch(
            setContractProcessingDataAction({
              items: itemsRef.current.map(item =>
                item.contractDateId === row.contractDateId ? { ...updatedItem, [field]: value } : item,
              ),
              totalItems: totalItemsRef.current,
            }),
          );
        } else {
          const request: PatchServiceDateRequest = {
            [field]: value,
          };
          const updatedItem = await patchServiceDate(row.serviceDateId, request, companyId);

          dispatch(
            setContractProcessingDataAction({
              items: itemsRef.current.map(item => (item.serviceDateId === row.serviceDateId ? updatedItem : item)),
              totalItems: totalItemsRef.current,
            }),
          );
        }

        setSuccessWithAutoDismiss('Field saved successfully');
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      }
    },
    [companyId, dispatch, setSuccessWithAutoDismiss],
  );

  // Selection helpers
  const allItemIds = useMemo(() => items.map(i => i.contractDateId), [items]);

  const currentPageSelectedRows = useMemo(() => {
    if (selectAllMode) {
      return allItemIds.filter(id => !excludedIds.has(id));
    }
    return selectedRows;
  }, [selectAllMode, allItemIds, excludedIds, selectedRows]);

  const isAllOnPageSelected = useMemo(() => {
    if (selectAllMode) {
      return allItemIds.every(id => !excludedIds.has(id));
    }
    return allItemIds.length > 0 && selectedRows.length === allItemIds.length;
  }, [selectAllMode, allItemIds, excludedIds, selectedRows]);

  const isSomeSelected = useMemo(() => {
    if (selectAllMode) {
      return excludedIds.size > 0 && excludedIds.size < totalItems;
    }
    return selectedRows.length > 0 && selectedRows.length < allItemIds.length;
  }, [selectAllMode, excludedIds.size, totalItems, selectedRows.length, allItemIds.length]);

  const handleClearAll = useCallback(() => {
    setSelectAllMode(false);
    setExcludedIds(new Set());
    setSelectedRows([]);
  }, []);

  const handleSelectAllOnPage = useCallback(() => {
    if (selectAllMode) {
      if (excludedIds.size > 0) {
        // Some excluded — clear exclusions to select all
        setExcludedIds(new Set());
      } else {
        // All selected — deselect all
        setSelectAllMode(false);
        setExcludedIds(new Set());
        setSelectedRows([]);
      }
    } else if (isAllOnPageSelected) {
      // All on page selected — deselect all
      setSelectedRows([]);
    } else {
      // Enter select-all mode
      setSelectAllMode(true);
      setExcludedIds(new Set());
      setSelectedRows([]);
    }
  }, [selectAllMode, excludedIds, isAllOnPageSelected]);

  const handleSelectionChange = useCallback(
    (ids: (string | number)[]) => {
      if (ids.length === 0) {
        handleClearAll();
        return;
      }

      if (selectAllMode) {
        const newExclusions = new Set(excludedIds);
        allItemIds.forEach(id => {
          if (!ids.includes(id)) {
            newExclusions.add(id);
          } else {
            newExclusions.delete(id);
          }
        });

        if (newExclusions.size >= totalItems) {
          setSelectAllMode(false);
          setExcludedIds(new Set());
          setSelectedRows([]);
        } else {
          setExcludedIds(newExclusions);
        }
      } else {
        setSelectedRows(ids as number[]);
      }
    },
    [selectAllMode, excludedIds, allItemIds, totalItems, handleClearAll],
  );

  const getSelectionCount = useCallback(() => {
    if (selectAllMode) {
      return totalItems - excludedIds.size;
    }
    return selectedRows.length;
  }, [selectAllMode, totalItems, excludedIds.size, selectedRows.length]);

  const handleBulkConfirm = useCallback(async () => {
    if (!companyId) return;
    setBulkConfirmLoading(true);
    try {
      const formattedDate = date ? dayjs(new Date(date)).format('DD/MM/YYYY') : undefined;
      const request: BulkConfirmServiceDatesRequest = {
        contractDateIds: selectAllMode ? [] : selectedRows,
        selectAll: selectAllMode,
        excludedContractDateIds: selectAllMode ? Array.from(excludedIds) : [],
        week: selectAllMode ? week : null,
        year: selectAllMode ? year : null,
        date: formattedDate,
        employeeIds: employeeIds?.length ? employeeIds : undefined,
        costCenterId: costCenterId || undefined,
        locationId: locationId || undefined,
        workerClass: workerClass || undefined,
        functionId: functionId || undefined,
      };
      const result = await dispatch(bulkConfirmServiceDatesAction({ request, companyId })).unwrap();

      // Reset selection state
      setSelectAllMode(false);
      setExcludedIds(new Set());
      setSelectedRows([]);

      // Trigger refetch by creating a new pagination object reference
      setPagination(prev => ({ ...prev }));

      if (result.length > 0) {
        setErrorMessage('Some rows could not be confirmed');
      } else {
        setSuccessWithAutoDismiss('All selected rows confirmed');
      }
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setBulkConfirmLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    selectAllMode,
    selectedRows,
    excludedIds,
    week,
    year,
    date,
    employeeIdsKey,
    costCenterId,
    locationId,
    workerClass,
    functionId,
    dispatch,
    setSuccessWithAutoDismiss,
  ]);

  return {
    items,
    totalItems,
    error,
    clearError,
    pagination,
    setPagination,
    sorting,
    setSorting,
    saveField,
    selectedRows,
    setSelectedRows,
    successMessage,
    clearSuccessMessage,
    errorMessage,
    clearErrorMessage,
    selectAllMode,
    excludedIds,
    currentPageSelectedRows,
    isAllOnPageSelected,
    isSomeSelected,
    handleSelectAllOnPage,
    handleSelectionChange,
    handleClearAll,
    getSelectionCount,
    handleBulkConfirm,
    bulkConfirmLoading,
    bulkConfirmErrors,
  };
};
